import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-form-hubspot',
  templateUrl: './form-hubspot.component.html',
  styleUrls: ['./form-hubspot.component.scss']
})
export class FormHubspotComponent implements OnInit {
  // Inputs configurables por si se embebe el componente de forma directa
  @Input() formId: string = "d745d73e-7630-47a2-95cf-d0c3e6f46132";
  @Input() portalId: string = "23647426";

  dynamicForm: FormGroup;
  conditionalRules : any = {}
  fields: any[] = [];
  loading = true;
  title = false;
  isSubmitted = false;
  selectedPrefix: string = '+54'; // Prefijo por defecto (Argentina)

  // Lista ordenada de países para el selector telefónico
  COUNTRIES_LIST = [
    { name: 'Argentina', code: 'AR', prefix: '+54' },
    { name: 'Bolivia', code: 'BO', prefix: '+591' },
    { name: 'Brasil', code: 'BR', prefix: '+55' },
    { name: 'Chile', code: 'CL', prefix: '+56' },
    { name: 'Colombia', code: 'CO', prefix: '+57' },
    { name: 'Costa Rica', code: 'CR', prefix: '+506' },
    { name: 'Cuba', code: 'CU', prefix: '+53' },
    { name: 'Ecuador', code: 'EC', prefix: '+593' },
    { name: 'El Salvador', code: 'SV', prefix: '+503' },
    { name: 'España', code: 'ES', prefix: '+34' },
    { name: 'Estados Unidos', code: 'US', prefix: '+1' },
    { name: 'Guatemala', code: 'GT', prefix: '+502' },
    { name: 'Honduras', code: 'HN', prefix: '+504' },
    { name: 'México', code: 'MX', prefix: '+52' },
    { name: 'Nicaragua', code: 'NI', prefix: '+505' },
    { name: 'Panamá', code: 'PA', prefix: '+507' },
    { name: 'Paraguay', code: 'PY', prefix: '+595' },
    { name: 'Perú', code: 'PE', prefix: '+51' },
    { name: 'Puerto Rico', code: 'PR', prefix: '+1' },
    { name: 'República Dominicana', code: 'DO', prefix: '+1' },
    { name: 'Uruguay', code: 'UY', prefix: '+598' },
    { name: 'Venezuela', code: 'VE', prefix: '+58' }
  ].sort((a, b) => a.name.localeCompare(b.name));

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private route: ActivatedRoute,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Si viene desde un Modal de Angular Material, extrae los parámetros de ahí
    this.title = data?.title;
    if (data?.formId) this.formId = data?.formId;
    if (data?.portalId) this.portalId = data?.portalId;

    this.dynamicForm = this.fb.group({});
  }

  ngOnInit() {
    this.fetchFields();
  }

  fetchFields() {
    const url = `/hubspot-api/forms/v2/fields/${this.formId}?portalId=${this.portalId}`;
    const headers = new HttpHeaders({
      'Authorization': `Bearer pat-na1-f951c2a4-e211-481f-bd51-4f74b5d280be`
    });

    this.http.get<any[]>(url, { headers }).subscribe({
      next: (response) => {
        this.processHubspotFields(response);
        this.buildForm();
        this.loading = false;
      },
      error: (err) => {
        console.error("Error cargando campos dinámicos de HubSpot", err);
        this.loading = false;
      }
    });
  }

  /**
   * Procesa la respuesta de HubSpot unificando campos principales y dependientes
   * previniendo cualquier tipo de duplicación visual en el formulario.
   */
  private processHubspotFields(rawFields: any[]) {
    const flattened: any[] = [];
    this.conditionalRules = {}; // Limpiamos las reglas en cada carga

    rawFields.forEach(field => {
      if (['mensaje', 'message', 'comentarios'].includes(field.name)) {
        field.fieldType = 'textarea';
      }

      if (!flattened.some(f => f.name === field.name)) {
        flattened.push(field);
      }

      // Extraer y guardar las reglas de dependencias de HubSpot
      if (field.dependentFieldFilters && field.dependentFieldFilters.length > 0) {
        if (!this.conditionalRules[field.name]) {
          this.conditionalRules[field.name] = [];
        }

        field.dependentFieldFilters.forEach((filterGroup: any) => {
          if (filterGroup.dependentFormField) {
            const depField = filterGroup.dependentFormField;

            if (['mensaje', 'message', 'comentarios'].includes(depField.name)) {
              depField.fieldType = 'textarea';
            }

            if (!flattened.some(f => f.name === depField.name)) {
              flattened.push(depField);
            }

            // Extraer los valores que activan este campo
            const validValues: string[] = [];
            filterGroup.filters?.forEach((filter: any) => {
              if (filter.strValues && filter.strValues.length > 0) {
                validValues.push(...filter.strValues);
              } else if (filter.strValue) {
                validValues.push(filter.strValue);
              }
            });

            // Guardar la regla: "Cuando [field.name] valga X, activar [depField.name]"
            this.conditionalRules[field.name].push({
              targetField: depField.name,
              values: validValues
            });
          }
        });
      }
    });

    this.fields = flattened;
  }
  onCountryChange(countryCode: string) {
    const country = this.COUNTRIES_LIST.find(c => c.code === countryCode);
    if (country) {
      this.selectedPrefix = country.prefix;
    }
  }

  buildForm() {
    this.fields.forEach(field => {
      const controlValidators = [];

      if (field.required) {
        controlValidators.push(Validators.required);
      }
      if (field.name === 'email') {
        controlValidators.push(Validators.email);
      }

      // Los controles se inicializan siempre vacíos
      this.dynamicForm.addControl(field.name, this.fb.control('', controlValidators));
    });

    // Inicializa observadores reactivos para la lógica condicional y autocompletado URL
    this.setupConditionalFields();
    this.autocompleteFromUrl();
  }

  private setupConditionalFields() {
    // Escuchar cambios en todos los campos que tengan campos dependientes
    Object.keys(this.conditionalRules).forEach(parentFieldName => {
      this.dynamicForm.get(parentFieldName)?.valueChanges.subscribe(() => {
        this.updateFieldsValidity();
      });
    });

    // Ocultar/deshabilitar dependientes al iniciar el formulario
    this.updateFieldsValidity();
  }

  private updateFieldsValidity() {
    // 1. Ocultar y resetear por defecto TODOS los campos dependientes
    const dependentFieldNames = new Set<string>();
    Object.values(this.conditionalRules).forEach((rules:any) => {
      rules.forEach(r => dependentFieldNames.add(r.targetField));
    });

    dependentFieldNames.forEach(targetName => {
      const control = this.dynamicForm.get(targetName);
      if (control) {
        control.disable({ emitEvent: false });
        control.setValue('', { emitEvent: false });
      }
    });

    // 2. Activar SOLO los que cumplan la condición actual seleccionada
    Object.keys(this.conditionalRules).forEach(parentFieldName => {
      const parentValue = this.dynamicForm.get(parentFieldName)?.value;

      if (parentValue) {
        this.conditionalRules[parentFieldName].forEach(rule => {
          if (rule.values.includes(parentValue)) {
            const targetControl = this.dynamicForm.get(rule.targetField);
            if (targetControl) {
              targetControl.enable({ emitEvent: false });
            }
          }
        });
      }
    });
  }

  /**
   * Manejador para Checkboxes Múltiples. 
   * HubSpot exige las respuestas múltiples unificadas en un string separado por ";"
   */
  onCheckboxChange(fieldName: string, optionValue: string, isChecked: boolean) {
    const control = this.dynamicForm.get(fieldName);
    if (!control) return;

    let currentValues = control.value ? control.value.split(';').filter((v: string) => v) : [];

    if (isChecked) {
      if (!currentValues.includes(optionValue)) {
        currentValues.push(optionValue);
      }
    } else {
      currentValues = currentValues.filter((v: string) => v !== optionValue);
    }

    control.setValue(currentValues.join(';'));
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remueve acentos
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_')
      .trim();
  }

  shouldShowField(field: any): boolean {
    const control = this.dynamicForm.get(field.name);
    return control ? !control.disabled : true;
  }

  isFullWidth(field: any): boolean {
    const isCarrera = field.name.includes('carreras_de_grado') || field.name.includes('carrera_de_posgrado');
    return field.fieldType === 'select' || field.fieldType === 'checkbox' || field.fieldType === 'radio' || field.fieldType === 'textarea' || isCarrera;
  }

  onSubmit() {
    if (this.dynamicForm.invalid) return;

    const formValue = this.dynamicForm.value;
    const submitUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${this.portalId}/${this.formId}`;

    // Construcción del payload respetando la API de envío de formularios de HubSpot
    const payload = {
      fields: Object.keys(formValue).map(key => {
        let finalValue = formValue[key];

        // Si es el campo de teléfono, le concatenamos el prefijo seleccionado
        if (key === 'phone' || key === 'mobilephone' || key === 'phone_ext') {
          finalValue = `${this.selectedPrefix}${finalValue}`;
        }
        return { name: key, value: finalValue };
      }),
      context: {
        pageUri: window.location.href,
        pageName: document.title
      }
    };

    this.http.post(submitUrl, payload).subscribe({
      next: () => {
        this.isSubmitted = true;
        Swal.fire('¡La consulta fue enviada con éxito!', 'Pronto nos pondremos en contacto.', 'success');
      },
      error: (err) => {
        console.error("Error al enviar el formulario a HubSpot", err);
        Swal.fire('La consulta falló', 'Por favor intenta nuevamente más tarde.', 'error');
      }
    });
  }

  /**
   * Autocompleta datos del formulario usando QueryParams de la URL (?email=test@uca.edu.ar)
   */
  private autocompleteFromUrl() {
    this.route.queryParams.subscribe(params => {
      Object.keys(params).forEach(key => {
        const control = this.dynamicForm.get(key);
        const fieldConfig = this.fields.find(f => f.name === key);
        if (!control) return;

        const valueFromUrl = decodeURIComponent(params[key]);

        if (fieldConfig?.fieldType === 'select') {
          const matchingOption = fieldConfig.options.find((opt: any) =>
            opt.label.trim().toLowerCase() === valueFromUrl.trim().toLowerCase() ||
            opt.value.toString() === valueFromUrl.toString()
          );

          if (matchingOption) {
            control.patchValue(matchingOption.value);
          }
        } else {
          control.patchValue(valueFromUrl);
        }
      });
      this.dynamicForm.updateValueAndValidity();
    });
  }
}
